import type { PermissionRequest } from '../../store/types'
import { BashSegments } from './BashSegments'
import { DiffView } from './DiffView'
import { Markdown } from './Markdown'
import { LabelRow } from './LabelRow'
import './ToolInput.css'

/// Tool-specific input renderers. Matches the SwiftUI ToolInputView
/// dispatch: bash, read/write/edit, webfetch, websearch, exitplanmode,
/// askuserquestion (non-interactive fallback for now), plus a generic
/// JSON dump for anything unrecognized.
///
/// Phase 2 keeps each renderer simple — key fields, mono font, no
/// syntax highlighting, no diff colorization. Pixel-fidelity
/// (risk-colored Bash segments, unified diffs, markdown plans) lands
/// in the polish task (#42).
export function ToolInput({ request }: { request: PermissionRequest }) {
  const tool = request.tool_name.toLowerCase()
  const input = request.tool_input

  switch (tool) {
    case 'bash':
      return <BashInput request={request} input={input} />
    case 'read':
      return <FilePathInput input={input} />
    case 'write':
      return <WriteInput input={input} />
    case 'edit':
    case 'multiedit':
      return <EditInput input={input} />
    case 'webfetch':
      return <WebFetchInput input={input} />
    case 'websearch':
      return <WebSearchInput input={input} />
    case 'exitplanmode':
      return <PlanInput input={input} />
    case 'askuserquestion':
      return <AskUserInput input={input} />
    default:
      return <FallbackInput input={input} />
  }
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v)
}

function BashInput({
  request,
  input,
}: {
  request: PermissionRequest
  input: Record<string, unknown>
}) {
  const command = asString(input.command)
  const description = asString(input.description)
  const segments = request.segments
  return (
    <div className="tool-input">
      {description && <div className="tool-input__description">{description}</div>}
      {/* Always the raw compound command, never per-segment text: the
          segmented view splits `a && b` for risk coloring, but what someone
          wants on their clipboard is the line they were asked to approve. */}
      <LabelRow label="Command" copy={command} />
      {segments && segments.length > 0 ? (
        <BashSegments segments={segments} />
      ) : (
        <pre className="tool-input__mono tool-input__mono--block">{command}</pre>
      )}
    </div>
  )
}

function FilePathInput({ input }: { input: Record<string, unknown> }) {
  const path = asString(input.file_path)
  return (
    <div className="tool-input">
      <LabelRow label="File" copy={path} />
      <code className="tool-input__mono">{path}</code>
    </div>
  )
}

function WriteInput({ input }: { input: Record<string, unknown> }) {
  const path = asString(input.file_path)
  const content = asString(input.content)
  const preview = content.length > 1200 ? content.slice(0, 1200) + '\n…' : content
  return (
    <div className="tool-input">
      <LabelRow label="File" copy={path} />
      <code className="tool-input__mono">{path}</code>
      {/* Copies the full content, not the 1200-char preview — the truncation
          is a rendering budget, not a statement about what was requested. */}
      <LabelRow label="Content" copy={content} />
      <pre className="tool-input__mono tool-input__mono--block">{preview}</pre>
    </div>
  )
}

function EditInput({ input }: { input: Record<string, unknown> }) {
  const path = asString(input.file_path)
  const edits = Array.isArray(input.edits) ? input.edits : null
  return (
    <div className="tool-input">
      <LabelRow label="File" copy={path} />
      <code className="tool-input__mono">{path}</code>
      {/* Per-edit copy takes the replacement text: the incoming half is the
          part you'd paste somewhere to check, and the outgoing half is
          already on disk if you want it. */}
      {edits ? (
        edits.map((e: any, i: number) => (
          <div key={i} className="tool-input__edit">
            <LabelRow
              label={edits.length > 1 ? `Edit ${i + 1}` : 'Change'}
              copy={asString(e.new_string)}
              copyLabel="Copy new text"
            />
            <DiffView before={asString(e.old_string)} after={asString(e.new_string)} />
          </div>
        ))
      ) : (
        <>
          <LabelRow
            label="Change"
            copy={asString(input.new_string)}
            copyLabel="Copy new text"
          />
          <DiffView
            before={asString(input.old_string)}
            after={asString(input.new_string)}
          />
        </>
      )}
    </div>
  )
}

function WebFetchInput({ input }: { input: Record<string, unknown> }) {
  const url = asString(input.url)
  return (
    <div className="tool-input">
      <LabelRow label="URL" copy={url} />
      <code className="tool-input__mono">{url}</code>
      {input.prompt != null && (
        <>
          <LabelRow label="Prompt" copy={asString(input.prompt)} />
          <div className="tool-input__text">{asString(input.prompt)}</div>
        </>
      )}
    </div>
  )
}

function WebSearchInput({ input }: { input: Record<string, unknown> }) {
  const query = asString(input.query)
  return (
    <div className="tool-input">
      <LabelRow label="Search" copy={query} />
      <div className="tool-input__text">{query}</div>
    </div>
  )
}

function PlanInput({ input }: { input: Record<string, unknown> }) {
  const plan = asString(input.plan)
  return (
    <div className="tool-input">
      {/* Copies the markdown source. Fenced blocks inside the rendered plan
          carry their own copy buttons (see Markdown.tsx) for grabbing just
          one snippet. */}
      <LabelRow label="Plan" copy={plan} />
      <div className="tool-input__md">
        <Markdown source={plan} />
      </div>
    </div>
  )
}

function AskUserInput({ input }: { input: Record<string, unknown> }) {
  const questions = Array.isArray(input.questions) ? input.questions : []
  const asText = questions.map((q: any) => asString(q.question ?? q)).join('\n')
  return (
    <div className="tool-input">
      <LabelRow label="Questions" copy={asText} />
      <ul className="tool-input__list">
        {questions.map((q: any, i: number) => (
          <li key={i}>{asString(q.question ?? q)}</li>
        ))}
      </ul>
    </div>
  )
}

function FallbackInput({ input }: { input: Record<string, unknown> }) {
  const json = JSON.stringify(input, null, 2)
  return (
    <div className="tool-input">
      <LabelRow label="Input" copy={json} />
      <pre className="tool-input__mono tool-input__mono--block">{json}</pre>
    </div>
  )
}
